import * as RA from "@radix-ui/react-avatar";
import clsx from "clsx";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

export type AvatarProps = {
  src?: string;
  alt?: string;
  fallback?: string;
  size?: AvatarSize;
  shape?: "circle" | "square";
  className?: string;
};

const sizeMap: Record<AvatarSize, string> = {
  xs: "size-6 text-xs",
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
  lg: "size-12 text-base",
  xl: "size-16 text-lg",
};

function initials(name?: string) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

export function Avatar({ src, alt, fallback, size = "md", shape = "circle", className }: AvatarProps) {
  return (
    <RA.Root
      className={clsx(
        "inline-flex items-center justify-center overflow-hidden bg-[var(--color-surface-muted)] text-[var(--color-text-primary)] font-medium select-none",
        shape === "circle" ? "rounded-full" : "rounded-[var(--radius-md)]",
        sizeMap[size],
        className,
      )}
    >
      {src && <RA.Image src={src} alt={alt ?? fallback ?? ""} className="size-full object-cover" />}
      <RA.Fallback className="size-full inline-flex items-center justify-center">
        {initials(fallback ?? alt)}
      </RA.Fallback>
    </RA.Root>
  );
}

export type TeamAvatarProps = {
  members: { name: string; src?: string }[];
  max?: number;
  size?: AvatarSize;
  className?: string;
};

export function TeamAvatar({ members, max = 4, size = "sm", className }: TeamAvatarProps) {
  const shown = members.slice(0, max);
  const overflow = members.length - shown.length;
  return (
    <span className={clsx("inline-flex -space-x-2", className)}>
      {shown.map((m, i) => (
        <span key={i} className="ring-2 ring-[var(--color-surface-base)] rounded-full">
          <Avatar size={size} src={m.src} fallback={m.name} alt={m.name} />
        </span>
      ))}
      {overflow > 0 && (
        <span className={clsx(
          "inline-flex items-center justify-center ring-2 ring-[var(--color-surface-base)] bg-[var(--color-surface-strong)] text-[var(--color-text-primary)] font-medium rounded-full",
          sizeMap[size],
        )}>
          +{overflow}
        </span>
      )}
    </span>
  );
}
